<template>
	<template v-if="programTimes">
		<p class="mt-6 text-sm" data-cy="program-times-text">
			{{ t('retreat_program_times', {
				start_time: programTimes.start_time,
				arrival_date: formattedArrivalDate,
				end_time: programTimes.end_time,
				departure_date: formattedDepartureDate,
			}) }}
		</p>
	</template>

	<template v-else>
		<p class="text-sm mt-6" data-cy="arrival-departure-intro">
			{{ t('choose_arrival_and_departure_time') }}
			<ToolTip class="ml-2">{{ t('arrival_and_departure_time_explanation') }}</ToolTip>
		</p>
		<div class="mt-4 shadow-card bg-white rounded divide-y divide-gray-100">
			<div class="grid 2xs:grid-cols-2 gap-4 items-center p-4 sm:px-6">
				<label for="arrivalTime" class="pr-4">
					{{ t('estimated_time_of_arrival') }}
				</label>
				<SelectTimeField
					id="arrivalTime"
					align="center"
					variant="outlined"
					data-cy="select-arrival-time"
					:disabled="$loadingstore.changing_date"
					:times="filteredArrivalTimes"
					:errors="$validationstore.errors.arrival_time"
					:model-value="$flowstore.checkout.data.arrival.time"
					@update:model-value="changeArrivalTime($event)"
				/>
			</div>
			<div class="grid 2xs:grid-cols-2 gap-4 items-center p-4 sm:px-6">
				<label for="departureTime" class="pr-4">
					{{ t('estimated_time_of_departure') }}
				</label>
				<SelectTimeField
					id="departureTime"
					align="center"
					variant="outlined"
					data-cy="select-departure-time"
					:disabled="$loadingstore.changing_date"
					:times="filteredDepartureTimes"
					:errors="$validationstore.errors.departure_time"
					:model-value="$flowstore.checkout.data.departure.time"
					@update:model-value="flowStore.setCheckoutTime('departure', $event)"
				/>
			</div>
		</div>
	</template>

	<Transition name="fade">
		<NoteBlock v-if="$validationstore.errors.arrival_date_and_time" variant="error" class="my-6 error-scroll-anchor">
			{{ $validationstore.errors.arrival_date_and_time[0] }}
		</NoteBlock>
	</Transition>

	<Transition name="fade">
		<NoteBlock v-if="$validationstore.errors.product_choices" variant="error" class="my-6 error-scroll-anchor">
			{{ $validationstore.errors.product_choices[0] }}
		</NoteBlock>
	</Transition>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useFlowStore } from '@/stores/useFlowStore';
import NoteBlock from '@/components/NoteBlock.vue';
import SelectTimeField from '@/components/fields/SelectTimeField.vue';
import ToolTip from '@/components/ToolTip.vue';
import { useI18n } from 'vue-i18n';
import { format, parse } from 'date-fns';
import { dateLocale as locale } from '@/config';

const { t } = useI18n();
const flowStore = useFlowStore();

const programTimes = computed(() => flowStore.checkout.meta?.program_times ?? null);

const formattedArrivalDate = computed(() => {
  const date = flowStore.checkout.data.arrival.date;
  if (!date) return '';
  return format(parse(date, 'yyyy-MM-dd', new Date()), 'd MMMM', { locale });
});

const formattedDepartureDate = computed(() => {
  const date = flowStore.checkout.data.departure.date;
  if (!date) return '';
  return format(parse(date, 'yyyy-MM-dd', new Date()), 'd MMMM', { locale });
});


async function changeArrivalTime(arrivalTime: string) {
  if (!flowStore.checkout.meta.departure_times) return;

  const departureTimesEntries = Object.entries(flowStore.checkout.meta.departure_times);
  const arrivalTimeIndex = departureTimesEntries.findIndex(([key]) => key === arrivalTime);
  const departureTimeIndex = departureTimesEntries.findIndex(([key]) => key === flowStore.checkout.data.departure.time);
  const isSameDay = flowStore.checkout.data.arrival.date === flowStore.checkout.data.departure.date;

  if (isSameDay && departureTimeIndex >= 0 && arrivalTimeIndex >= departureTimeIndex) {
    const nextAvailableDepartureTime = (arrivalTimeIndex + 1) < departureTimesEntries.length
      ? departureTimesEntries[arrivalTimeIndex + 1][0]
      : departureTimesEntries[departureTimesEntries.length - 1][0];

    if (nextAvailableDepartureTime) {
      return await flowStore.setCheckoutTimes(arrivalTime, nextAvailableDepartureTime);
    }
  }

  await flowStore.setCheckoutTime('arrival', arrivalTime);
}

const filteredArrivalTimes = computed(() => {
  if (!flowStore.checkout.meta.arrival_times) return {};

  const arrivalTimesEntries = Object.entries(flowStore.checkout.meta.arrival_times);

  const arrivalTimesFilteredEntries = arrivalTimesEntries.filter(([, available]) => {
    return available === true;
  });

  return Object.fromEntries(arrivalTimesFilteredEntries);
});

const filteredDepartureTimes = computed(() => {
  if (!flowStore.checkout.meta.departure_times) return {};

  const departureTimesEntries = Object.entries(flowStore.checkout.meta.departure_times);
  const departureTimeIndex = departureTimesEntries.findIndex(([key]) => key === flowStore.checkout.data.arrival.time);
  const isSameDay = flowStore.checkout.data.arrival.date === flowStore.checkout.data.departure.date;

  const departureTimesFilteredEntries = departureTimesEntries.filter(([, available], index) => {
      if (!available) return false;
      if (!isSameDay) return true;

      return departureTimeIndex !== undefined && index > departureTimeIndex;
  });

  return Object.fromEntries(departureTimesFilteredEntries);
});
</script>
